/**
 * Wishlist functionality for EEL theme
 * Stores wishlist items in localStorage
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'eel_wishlist';

  const Wishlist = {
    /**
     * Get all wishlist items
     * @returns {Array} Array of product objects
     */
    getAll: function() {
      try {
        const data = localStorage.getItem(STORAGE_KEY);
        return data ? JSON.parse(data) : [];
      } catch (e) {
        console.warn('Wishlist: Unable to read from localStorage', e);
        return [];
      }
    },

    /**
     * Save wishlist to localStorage
     * @param {Array} items - Array of product objects
     */
    save: function(items) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      } catch (e) {
        console.warn('Wishlist: Unable to save to localStorage', e);
      }
    },

    /**
     * Check if a product is in the wishlist
     * @param {string|number} productId - Product ID to check
     * @returns {boolean}
     */
    isInWishlist: function(productId) {
      const items = this.getAll();
      return items.some(function(item) {
        return String(item.id) === String(productId);
      });
    },

    /**
     * Add a product to the wishlist
     * @param {Object} product - Product object with id, handle, title, image, price, url
     * @returns {boolean} True if added, false if already exists
     */
    add: function(product) {
      if (this.isInWishlist(product.id)) {
        return false;
      }
      const items = this.getAll();
      items.push({
        id: String(product.id),
        handle: product.handle,
        title: product.title,
        image: product.image,
        price: product.price,
        url: product.url,
        variantId: product.variantId,
        variantAvailable: product.variantAvailable
      });
      this.save(items);
      this.updateAllButtons();
      this.dispatchEvent('add', product);
      return true;
    },

    /**
     * Remove a product from the wishlist
     * @param {string|number} productId - Product ID to remove
     * @returns {boolean} True if removed, false if not found
     */
    remove: function(productId) {
      const items = this.getAll();
      const initialLength = items.length;
      const filtered = items.filter(function(item) {
        return String(item.id) !== String(productId);
      });
      if (filtered.length === initialLength) {
        return false;
      }
      this.save(filtered);
      this.updateAllButtons();
      this.dispatchEvent('remove', { id: productId });
      return true;
    },

    /**
     * Toggle a product in the wishlist
     * @param {Object} product - Product object
     * @returns {boolean} True if now in wishlist, false if removed
     */
    toggle: function(product) {
      if (this.isInWishlist(product.id)) {
        this.remove(product.id);
        return false;
      } else {
        this.add(product);
        return true;
      }
    },

    /**
     * Get wishlist count
     * @returns {number}
     */
    getCount: function() {
      return this.getAll().length;
    },

    /**
     * Update all wishlist buttons on the page
     */
    updateAllButtons: function() {
      var self = this;
      document.querySelectorAll('[data-wishlist-button]').forEach(function(button) {
        var productId = button.getAttribute('data-product-id');
        if (self.isInWishlist(productId)) {
          button.classList.add('is-active');
          button.setAttribute('aria-pressed', 'true');
        } else {
          button.classList.remove('is-active');
          button.setAttribute('aria-pressed', 'false');
        }
      });
    },

    /**
     * Dispatch custom event
     * @param {string} action - Event action (add, remove)
     * @param {Object} product - Product data
     */
    dispatchEvent: function(action, product) {
      document.dispatchEvent(new CustomEvent('wishlist:changed', {
        detail: { action: action, product: product, count: this.getCount() }
      }));
    },

    /**
     * Initialize wishlist functionality
     */
    init: function() {
      var self = this;

      // Update buttons on page load
      this.updateAllButtons();

      // Handle button clicks using event delegation
      document.addEventListener('click', function(e) {
        var button = e.target.closest('[data-wishlist-button]');
        if (!button) return;

        e.preventDefault();
        e.stopPropagation();

        var product = {
          id: button.getAttribute('data-product-id'),
          handle: button.getAttribute('data-product-handle'),
          title: button.getAttribute('data-product-title'),
          image: button.getAttribute('data-product-image'),
          price: button.getAttribute('data-product-price'),
          url: button.getAttribute('data-product-url'),
          variantId: button.getAttribute('data-variant-id'),
          variantAvailable: button.getAttribute('data-variant-available')
        };

        self.toggle(product);
      });

      // Sync across tabs
      window.addEventListener('storage', function(e) {
        if (e.key === STORAGE_KEY) {
          self.updateAllButtons();
          self.dispatchEvent('sync', null);
        }
      });

      // Handle dynamic content (e.g., quick view modals, infinite scroll)
      var observer = new MutationObserver(function(mutations) {
        var hasNewButtons = mutations.some(function(mutation) {
          return Array.from(mutation.addedNodes).some(function(node) {
            return node.nodeType === 1 && (
              node.hasAttribute && node.hasAttribute('data-wishlist-button') ||
              node.querySelector && node.querySelector('[data-wishlist-button]')
            );
          });
        });
        if (hasNewButtons) {
          self.updateAllButtons();
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      Wishlist.init();
    });
  } else {
    Wishlist.init();
  }

  // Expose to global scope
  window.EELWishlist = Wishlist;
})();
